import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, Paper, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, Tooltip, Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import BedIcon from "@mui/icons-material/Bed";
import EditBookingGroup from "../Booking Groups/EditBookingGroup";
import { GANTT_STATUSES, ganttMeta, statusLabel } from "../Booking Groups/utils/bookingStatus";
import { isAdmin } from "../shared utils/auth";
import CapacityDialog from "./CapacityDialog";
import IndependentStudentsDialog from "./IndependentStudentsDialog";
import { getBookingGantt, listBookingGroups, listCampusSeasons } from "./api/bookingGanttApi";
import {
  barCells, barLabel, dayOfMonth, formatNumber, indieShade, monthSpans, seasonLabel, seasonsInYear,
  shortDate, TOTAL_ROWS, weekday,
} from "./utils/ganttLayout";

// Replaces the "GANTT Summer latest" and "Bed Summary" sheets: one section per
// centre, a column per night, a bar per booking group coloured by status, the
// independent students as one shaded row, nightly bed totals underneath and
// the weekly lowest beds remaining at the bottom.

const LABEL_W = 240;
const DAY_W = 30;
const GREY = "#e0e0e0";
const RED_BG = "#ffebee";

const stickyLabel = {
  position: "sticky",
  left: 0,
  zIndex: 2,
  bgcolor: "background.paper",
  minWidth: LABEL_W,
  maxWidth: LABEL_W,
  width: LABEL_W,
  borderRight: "1px solid #ddd",
};

const dayCell = { minWidth: DAY_W, maxWidth: DAY_W, width: DAY_W, p: 0, textAlign: "center" };

export default function BookingGanttPage({ selectedYear, showFinancials = false, onSelectStudent }) {
  const year = Number(selectedYear) || new Date().getFullYear();
  const admin = isAdmin();

  const [campuses, setCampuses] = useState([]);
  const [selected, setSelected] = useState(null); // Set of campus ids; null = not chosen yet
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);

  const [groupsCache, setGroupsCache] = useState(null);
  const [openGroup, setOpenGroup] = useState(null);
  const [indiesFor, setIndiesFor] = useState(null);
  const [capacityFor, setCapacityFor] = useState(null);

  // Seasons for the year: every one selected to begin with.
  useEffect(() => {
    let cancelled = false;
    listCampusSeasons()
      .then((rows) => {
        if (cancelled) return;
        setCampuses(rows);
        setSelected(new Set(seasonsInYear(rows, year).map((c) => c.id)));
      })
      .catch((e) => !cancelled && setError(e.message || "Could not load the centres"));
    return () => { cancelled = true; };
  }, [year]);

  const seasons = useMemo(() => seasonsInYear(campuses, year), [campuses, year]);

  useEffect(() => {
    if (!selected) return;
    if (selected.size === 0) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    getBookingGantt({ year, campusIds: [...selected] })
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setError(e.message || "Could not load the Gantt"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [year, selected, reload]);

  const refresh = useCallback(() => {
    setGroupsCache(null);
    setReload((r) => r + 1);
  }, []);

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev || []);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // The edit dialog wants the group as the Groups page lists it.
  const openBar = async (bar) => {
    try {
      let groups = groupsCache;
      if (!groups) {
        groups = await listBookingGroups();
        setGroupsCache(groups);
      }
      const g = groups.find((x) => x.id === bar.groupId);
      if (g) setOpenGroup(g);
      else setError(`Booking group ${bar.groupId} could not be found`);
    } catch (e) {
      setError(e.message || "Could not open the group");
    }
  };

  const dates = useMemo(() => (data?.centres?.[0]?.days || []).map((d) => d.date), [data]);
  const months = useMemo(() => monthSpans(dates), [dates]);
  const activeCampuses = useMemo(() => campuses.filter((c) => c.active !== false), [campuses]);

  return (
    <Paper sx={{ p: { xs: 1, md: 2 }, m: { xs: 0.5, md: 1 } }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexWrap: "wrap", rowGap: 1 }}>
        <Typography variant="h5" sx={{ mr: 2 }}>Groups Gantt {year}</Typography>
        {seasons.map((c) => (
          <Chip
            key={c.id}
            label={seasonLabel(c, seasons)}
            color={selected?.has(c.id) ? "primary" : "default"}
            variant={selected?.has(c.id) ? "filled" : "outlined"}
            onClick={() => toggle(c.id)}
          />
        ))}
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={refresh} disabled={loading}><RefreshIcon /></IconButton>
          </span>
        </Tooltip>
        {loading ? <CircularProgress size={20} /> : null}
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mb: 1, flexWrap: "wrap", rowGap: 0.5 }}>
        {GANTT_STATUSES.map((s) => (
          <Stack key={s.key} direction="row" spacing={0.5} alignItems="center">
            <Box sx={{ width: 16, height: 12, bgcolor: s.color, border: "1px solid #999" }} />
            <Typography variant="caption">{s.label}</Typography>
          </Stack>
        ))}
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box sx={{ width: 16, height: 12, bgcolor: indieShade(1, 1), border: "1px solid #999" }} />
          <Typography variant="caption">Independent students (darker = more)</Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box sx={{ width: 16, height: 12, bgcolor: GREY, border: "1px solid #999" }} />
          <Typography variant="caption">Centre unavailable</Typography>
        </Stack>
      </Stack>

      {error ? <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError("")}>{error}</Alert> : null}

      {!seasons.length && selected ? (
        <Alert severity="info">No centres are set up for {year}. Add the seasons under Admin → Campuses.</Alert>
      ) : null}

      {data?.centres?.length ? (
        <>
          <Paper variant="outlined" sx={{ overflow: "auto", maxHeight: "calc(100vh - 230px)" }}>
            <Table size="small" sx={{
              width: LABEL_W + dates.length * DAY_W, minWidth: LABEL_W + dates.length * DAY_W,
              borderCollapse: "separate", "& td, & th": { borderRight: "1px solid #eee" },
            }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...stickyLabel, top: 0, zIndex: 4 }} />
                  {months.map((m) => (
                    <TableCell key={m.label} colSpan={m.span} sx={{ position: "sticky", top: 0, zIndex: 3, bgcolor: "background.paper", fontWeight: 600, py: 0.5, whiteSpace: "nowrap" }}>
                      {m.label}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell sx={{ ...stickyLabel, top: 29, zIndex: 4 }} />
                  {dates.map((iso) => {
                    const wd = weekday(iso);
                    return (
                      <TableCell key={iso} sx={{
                        ...dayCell, position: "sticky", top: 29, zIndex: 3, bgcolor: "background.paper",
                        fontSize: 11, lineHeight: 1.2, py: 0.25,
                        borderLeft: wd === "Su" ? "2px solid #bbb" : undefined,
                        fontWeight: wd === "Su" || wd === "We" ? 700 : 400,
                      }}>
                        {wd}<br />{dayOfMonth(iso)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.centres.map((centre) => (
                  <CentreSection
                    key={centre.campusId}
                    centre={centre}
                    admin={admin}
                    onOpenBar={openBar}
                    onOpenIndies={() => setIndiesFor(centre)}
                    onEditCapacity={() => setCapacityFor(centre)}
                  />
                ))}
              </TableBody>
            </Table>
          </Paper>

          <WeeklySummary data={data} />
        </>
      ) : null}

      {openGroup ? (
        <EditBookingGroup
          open
          group={openGroup}
          campuses={activeCampuses}
          showFinancials={showFinancials}
          initialTab="members"
          onClose={() => setOpenGroup(null)}
          onUpdated={refresh}
          onMembersChanged={() => refresh()}
        />
      ) : null}

      <IndependentStudentsDialog
        open={Boolean(indiesFor)}
        centre={indiesFor}
        onClose={() => setIndiesFor(null)}
        onSelectStudent={onSelectStudent ? (id) => { setIndiesFor(null); onSelectStudent(id); } : undefined}
      />

      <CapacityDialog
        open={Boolean(capacityFor)}
        centre={capacityFor}
        onClose={() => setCapacityFor(null)}
        onSaved={refresh}
      />
    </Paper>
  );
}

function CentreSection({ centre, admin, onOpenBar, onOpenIndies, onEditCapacity }) {
  const days = centre.days;
  const dates = days.map((d) => d.date);
  const s = centre.summary;
  const greyAt = (i) => (days[i].available ? undefined : GREY);

  return (
    <>
      <TableRow>
        <TableCell colSpan={days.length + 1} sx={{ bgcolor: "#f5f5f5", borderTop: "3px solid #9e9e9e", py: 0.75 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ position: "sticky", left: 8, display: "inline-flex", flexWrap: "wrap", rowGap: 0.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{centre.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {shortDate(centre.arrival)} to {shortDate(centre.departure)}
            </Typography>
            <Typography variant="body2">
              Student nights <b>{formatNumber(s.studentNights)}</b>
              {" · "}Student weeks <b>{formatNumber(s.studentWeeks, 1)}</b>
              {" · "}Min guarantee <b>{s.minGuaranteeStudentWeeks ?? "—"}</b>
              {s.minGuaranteeStudentWeeks != null ? <>{" · "}Still needed <b>{formatNumber(s.studentWeeksStillNeeded, 1)}</b></> : null}
              {" · "}Full <b>{s.percentFull == null ? "—" : `${formatNumber(s.percentFull, 1)}%`}</b>
            </Typography>
            {admin ? (
              <Button size="small" startIcon={<BedIcon />} onClick={onEditCapacity}>Beds &amp; guarantee</Button>
            ) : null}
            {!centre.capacity?.length ? (
              <Typography variant="caption" color="warning.main">No bed capacity entered</Typography>
            ) : null}
          </Stack>
        </TableCell>
      </TableRow>

      {centre.groups.map((bar) => {
        const meta = ganttMeta(bar.ganttStatus);
        return (
          <TableRow key={bar.groupId} hover>
            <TableCell sx={{ ...stickyLabel, py: 0.25 }}>
              <Typography variant="body2" noWrap title={barLabel(bar)}>{bar.name}</Typography>
            </TableCell>
            {barCells(dates, bar.arrival, bar.departure).map((c) => (c.kind === "empty" ? (
              <TableCell key={c.index} sx={{ ...dayCell, bgcolor: greyAt(c.index) }} />
            ) : (
              <TableCell key={c.index} colSpan={c.span} sx={{ p: 0.25 }}>
                <Tooltip title={`${barLabel(bar)} · ${statusLabel(bar.status) || meta.label} · ${shortDate(bar.arrival)} to ${shortDate(bar.departure)}`}>
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => onOpenBar(bar)}
                    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpenBar(bar)}
                    sx={{
                      bgcolor: meta.color, border: "1px solid rgba(0,0,0,0.35)", borderRadius: 0.5,
                      px: 0.5, fontSize: 12, lineHeight: "20px", height: 22, cursor: "pointer",
                      // Fixed to the nights it covers so a long label can't widen the day columns.
                      width: c.span * DAY_W - 4, boxSizing: "border-box",
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "#111",
                      borderLeftStyle: c.clippedStart ? "dashed" : "solid",
                      borderRightStyle: c.clippedEnd ? "dashed" : "solid",
                    }}
                  >
                    {barLabel(bar)}
                  </Box>
                </Tooltip>
              </TableCell>
            )))}
          </TableRow>
        );
      })}

      {centre.indies ? (
        <TableRow hover sx={{ cursor: "pointer" }} onClick={onOpenIndies}>
          <TableCell sx={{ ...stickyLabel, py: 0.25 }}>
            <Typography variant="body2" noWrap>
              Independent students <Typography component="span" variant="caption" color="text.secondary">(up to {centre.indies.peak})</Typography>
            </Typography>
          </TableCell>
          {centre.indies.daily.map((n, i) => (
            <TableCell key={i} sx={{
              ...dayCell, fontSize: 11,
              bgcolor: n ? indieShade(n, centre.indies.peak) : greyAt(i),
              color: n && n / Math.max(1, centre.indies.peak) > 0.6 ? "#fff" : undefined,
            }}>
              {n || ""}
            </TableCell>
          ))}
        </TableRow>
      ) : null}

      {!centre.groups.length && !centre.indies ? (
        <TableRow>
          <TableCell sx={{ ...stickyLabel, py: 0.25 }}>
            <Typography variant="body2" color="text.secondary">No bookings yet</Typography>
          </TableCell>
          {days.map((d, i) => <TableCell key={d.date} sx={{ ...dayCell, bgcolor: greyAt(i) }} />)}
        </TableRow>
      ) : null}

      {TOTAL_ROWS.map((row) => (
        <TableRow key={row.key}>
          <TableCell sx={{ ...stickyLabel, py: 0.25, bgcolor: "#fafafa" }}>
            <Typography variant="body2" sx={{ fontWeight: row.strong ? 700 : 400 }}>{row.label}</Typography>
          </TableCell>
          {days.map((d, i) => {
            const v = d[row.key];
            const negative = row.warnBelowZero && v != null && v < 0;
            const blank = !d.available && !v;
            return (
              <TableCell key={d.date} sx={{
                ...dayCell, fontSize: 11,
                fontWeight: row.strong ? 700 : 400,
                bgcolor: negative ? RED_BG : greyAt(i) || "#fafafa",
                color: negative ? "error.main" : undefined,
              }}>
                {blank || v == null ? "" : v}
              </TableCell>
            );
          })}
        </TableRow>
      ))}
    </>
  );
}

function WeeklySummary({ data }) {
  const names = Object.fromEntries(data.centres.map((c) => [c.campusId, c.name]));
  const block = (title, rows) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{title}</Typography>
      <Paper variant="outlined" sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 180 }}>Week from</TableCell>
              {rows.map((r) => <TableCell key={r.weekStart} align="right">{shortDate(r.weekStart)}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.centres.map((c, ci) => (
              <TableRow key={c.campusId}>
                <TableCell>{names[c.campusId]}</TableCell>
                {rows.map((r) => {
                  const v = r.centres[ci]?.lowestRemaining;
                  return (
                    <TableCell key={r.weekStart} align="right" sx={{ color: v != null && v < 0 ? "error.main" : undefined }}>
                      {v ?? ""}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>All centres</TableCell>
              {rows.map((r) => (
                <TableCell key={r.weekStart} align="right" sx={{ fontWeight: 700, color: r.total != null && r.total < 0 ? "error.main" : undefined }}>
                  {r.total ?? ""}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>Lowest beds remaining by week</Typography>
      {block("Sunday changeover", data.weeklySunday || [])}
      {block("Wednesday changeover", data.weeklyWednesday || [])}
    </Box>
  );
}
